/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/featuresSection.config.json";
import jwt_decode from "jwt-decode";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_MY_PROFILE } from "../graphql";
import { VApplication } from "../../../application";
import { StorageModule } from "@vlinder-web/storage-module-react";
import moment from "moment";
import { toHeaderCase } from "js-convert-case";

import { AppNavigatorService } from "../../appNavigatorService";

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.AppNavigatorService") as AppNavigatorService;
};

export interface IFeatureSectionMethods extends IPluginMethodMap {
  getFeatureSectionProps(args: any): Promise<any>;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const lookup = require("country-code-lookup");

export class FeatureSectionService implements IAgentPlugin {
  readonly methods: IFeatureSectionMethods;

  constructor() {
    this.methods = {
      getFeatureSectionProps: this.getProps.bind(this),
    };
  }

  async getProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const storageSrv = StorageModule?.getInstance();

    try {
      if (client) {
        const res = await client.query({
          query: GET_MY_PROFILE,
          variables: {},
          fetchPolicy: "network-only",
        });
        if (res?.data?.getMyProfile) {
          storageSrv?.set("myProfile", res?.data?.getMyProfile, "session");
          let _res = res?.data?.getMyProfile;
          return {
            title: `${toHeaderCase(_res?.firstName ?? "")} ${toHeaderCase(
              _res?.lastName ?? ""
            )}`,
            subtitle: _res?.vuid
              ? `Account ID: ${_res?.vuid}`
              : _res?.occupation,
            heroImage: {
              image:
                _res?.image ??
                `https://ui-avatars.com/api/?name=${_res.firstName}+${_res?.lastName}&background=0D8ABC&color=fff`
            },
            listItems: this.getListItems(_res),
            initialData: {
              firstName: _res?.firstName,
              lastName: _res?.lastName,
              image: _res?.image,
              telephone: _res?.primaryContactNo,
              address: _res?.address?.addressLine1,
              city: _res?.address?.city,
              country: _res?.address?.country,
              occupation: _res?.occupation,
            },
            buttons: config?.buttons,
            organizationProfile: {
              title: _res?.org?.name,
              subtitle: _res?.org?.description,
              heroImage: {
                image:
                  _res?.org?.logo ??
                  `https://ui-avatars.com/api/?name=${_res.org?.name}&background=0D8ABC&color=fff`
              },
              listItems: this.getOrgListItems(_res?.org),
              buttons: config?.buttons,
              formDefaultData: {
                name: _res?.org?.name,
                description: _res?.org?.description,
                image: _res?.org?.logo,
                address: _res?.org?.address?.addressLine1,
                country: _res?.org?.address?.country,
                city: _res?.org?.address?.city,
                telephone: _res?.org?.primaryContactNo,
                website: _res?.org?.website,
              },
            },
          };
        }
      }
    } catch (err: any) {
      console.log(`Sign In user error`, err?.response?.data);
      throw Error(err?.response?.data?.error?.display_message);
    }
  }

  private getOrgListItems(res: any) {
    let _temp = [];
    _temp = config?.orgListItems?.map((item) => {
      if (item?.title === "Address") {
        return {
          ...item,
          subTitle: res?.address?.addressLine1
            ? `${res?.address?.addressLine1}`
            : "-",
        };
      }
      if (item?.title === "City") {
        return {
          ...item,
          subTitle: res?.address?.city ?? "-",
        };
      }

      if (item?.title === "Country") {
        return {
          ...item,
          subTitle:
            res?.address?.country && res?.address?.country.length === 2
              ? lookup.byIso(`${res?.address?.country}`)?.country
              : "-",
          flag: res?.address?.country,
        };
      }
      if (item?.title === "Email") {
        return {
          ...item,
          subTitle: res?.email ?? "-",
        };
      }
      if (item?.title === "Phone Number") {
        return {
          ...item,
          subTitle: res?.primaryContactNo ?? "-",
        };
      }
      if (item?.title === "Website") {
        return {
          ...item,
          subTitle: res?.website ?? "-",
        };
      }
    });
    return _temp;
  }

  private getListItems(res: any) {
    let _temp = [];
    _temp = config?.listItems?.map((item) => {
      if (item?.title === "Full Name") {
        return {
          ...item,
          subTitle: `${toHeaderCase(res?.firstName ?? "")} ${toHeaderCase(
            res?.lastName ?? ""
          )}`,
        };
      }
      if (item?.title === "Street Address") {
        return {
          ...item,
          subTitle: res?.address?.addressLine1
            ? `${res?.address?.addressLine1}`
            : "-",
        };
      }
      if (item?.title === "City") {
        return {
          ...item,
          subTitle: res?.address?.city ?? "-",
        };
      }
      if (item?.title === "Country") {
        return {
          ...item,
          subTitle:
            res?.address?.country && res?.address?.country.length === 2
              ? lookup.byIso(`${res?.address?.country}`)?.country
              : "-",
          flag: res?.address?.country,
        };
      }
      if (item?.title === "Email") {
        return {
          ...item,
          subTitle: res?.email ?? "-",
          verified: res?.emailVerified,
        };
      }
      if (item?.title === "Telephone") {
        return {
          ...item,
          subTitle: res?.primaryContactNo ?? "-",
          verified: res?.phoneVerified,
        };
      }
      if (item?.title === "Occupation") {
        return {
          ...item,
          subTitle: res?.occupation ?? "-",
        };
      }
    });
    return _temp;
  }
}
